import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { NotificationsType } from 'src/api';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { SettingsTooltip } from 'src/components/molecules';
import {
  clearSession,
  fetchPublicMessages,
} from 'src/creators/OpenReporting.creators';
import {
  useGetGlobalFeaturesQuery,
  useGetPublicFeaturesQuery,
} from 'src/graphql';
import { useNotifications } from 'src/hooks/useNotifications';
import { AppDispatch } from 'src/store';
import { useTheme } from 'styled-components';
import * as Types from 'src/types';
import { NavbarVariantEnum, User } from 'src/types';
import { getInitials } from 'src/utils/getInitials';
import * as Intercom from 'src/utils/intercom';
import { BaseNavbar } from './BaseNavbar';
import {
  AccountNavLink,
  AccountText,
  AccountTextContainer,
  LinkWithBadgeContainer,
  NavBarIconNotificationContainer,
  ProfileContainer,
  PublicNavbarBottomMenu,
  StyledNavAvatar,
  StyledNavBarIcon,
  StyledNotificationBadge,
} from './BaseNavbar.styles';
import { useNavbarContext } from './NavbarProvider';
import { NavLink } from './components/NavLink';
import { NotificationsList } from './components/NotificationsList';

interface Props {
  organisation: string;
  currentUser?: User;
}

const NavMenu = ({ organisation }: { organisation: string }) => {
  const { t } = useTranslation();
  const { isOpen } = useNavbarContext();

  return (
    <>
      <NavLink
        to={`/${organisation}/open-reporting/reports`}
        data-testid="public-reports-navbar"
        icon="uil uil-file-alt"
        text={t('general.navbar.reporter.myReports')}
        navOpen={Boolean(isOpen)}
        tooltipText="My reports"
        variant={NavbarVariantEnum.PUBLIC}
      />
      <NavLink
        to={`/${organisation}/open-reporting/messages`}
        data-testid="public-messages-navbar"
        icon="uil uil-envelope"
        text={t('public.PublicMessageList.myMessages')}
        navOpen={Boolean(isOpen)}
        tooltipText="Messages"
        variant={NavbarVariantEnum.PUBLIC}
      />
    </>
  );
};

const AccountMenu = ({
  currentUser,
  organisation,
}: {
  currentUser?: User;
  organisation: string;
}) => {
  const { t } = useTranslation();
  const settingsTooltipRef = useRef<HTMLDivElement>(null);
  const [settingsTooltipRoot, setSettingsTooltipRoot] =
    useState<HTMLElement | null>(null);
  const [settingsTooltip, setSettingsTooltip] = useState(false);

  const { isOpen, isSecondaryDrawerOpen, setSecondaryDrawerOpen } =
    useNavbarContext();

  const { data: featuresData } = useGetGlobalFeaturesQuery();
  const { data: publicFeatures } = useGetPublicFeaturesQuery({
    slug: organisation,
  });

  const globalFlags =
    (featuresData?.getGlobalFeatures as Types.Feature[]) ?? [];
  const publicFlags =
    (publicFeatures?.getPublicFeatures as Types.Feature[]) ?? [];

  const isDynamicTranslationsEnabled = isRequiredFeatureEnabled(
    [...publicFlags, ...globalFlags],
    Types.FeatureFlag.DYNAMIC_TRANSLATIONS,
  );

  const messageSettings = {
    text: t('public.UserSettingsDialog.messageSettings'),
    linkTo: `/${organisation}/open-reporting/message-settings`,
    logout: false,
    iconClassName: 'uil uil-suitcase',
  };

  const settingsOptions = [
    ...(isDynamicTranslationsEnabled ? [messageSettings] : []),
  ];

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const theme = useTheme();
  const { numberOfNotifications, hasNotifications } = useNotifications();

  const onLogout = () => {
    dispatch(clearSession());
    navigate(`/${organisation}/open-reporting/login`, { replace: true });
  };

  return (
    <PublicNavbarBottomMenu
      open={isOpen}
      variant={NavbarVariantEnum.PUBLIC}
      responsive={true}
    >
      {/* Notifications */}
      <AccountNavLink
        variant={NavbarVariantEnum.PUBLIC}
        data-testid="public-notifications-navbar"
        notificationOpen={isSecondaryDrawerOpen}
        onClick={() => setSecondaryDrawerOpen(!isSecondaryDrawerOpen)}
      >
        <LinkWithBadgeContainer open={isOpen} notification={hasNotifications}>
          <NavBarIconNotificationContainer
            open={isOpen}
            notification={hasNotifications}
          >
            <StyledNotificationBadge
              open={!isOpen}
              variant="custom"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N0
                  : theme.atlas.semantic.color.type.inverse
              }
              background-color={
                theme.isVaultVariant
                  ? theme.newPalette.turquoise.T300
                  : theme.atlas.core.color.brand.diligentRed
              }
            />
            <StyledNavBarIcon className="uil uil-bell" size="20px" />
          </NavBarIconNotificationContainer>
          <AccountText open={isOpen}>
            {t('general.navbar.notifications')}
          </AccountText>
          {hasNotifications && (
            <StyledNotificationBadge
              open={isOpen}
              data-testid="public-notifications-badge-count"
              variant="custom"
              text={numberOfNotifications?.toString()}
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N0
                  : theme.atlas.semantic.color.type.inverse
              }
              background-color={
                theme.isVaultVariant
                  ? theme.newPalette.turquoise.T300
                  : theme.atlas.core.color.brand.diligentRed
              }
            />
          )}
        </LinkWithBadgeContainer>
      </AccountNavLink>

      {/* Technical Support */}
      <AccountNavLink
        variant={NavbarVariantEnum.PUBLIC}
        data-testid="public-technical-support-navbar"
        onClick={() => (Intercom.isOpen ? Intercom.hide() : Intercom.show())}
      >
        <StyledNavBarIcon className="uil uil-chat" size="20px" />
        <AccountText open={isOpen}>
          {t('general.navbar.technicalSupport')}
        </AccountText>
      </AccountNavLink>

      {/* User Profile */}
      {currentUser && (
        <div ref={settingsTooltipRef}>
          <ProfileContainer
            variant={NavbarVariantEnum.PUBLIC}
            settingsTooltip={settingsTooltip}
            ref={setSettingsTooltipRoot}
            onClick={() => setSettingsTooltip(!settingsTooltip)}
            data-testid="public-account-button"
          >
            <StyledNavAvatar
              dark={true}
              key={currentUser.name}
              variant="text"
              text={currentUser.name ? getInitials(currentUser.name) : 'A'}
              size="small"
              textColor={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N800
                  : theme.atlas.semantic.color.type.default
              }
            />
            {isOpen && (
              <AccountTextContainer open={isOpen}>
                <AccountText open={isOpen}>
                  {currentUser.name ? currentUser.name : 'Anonymous'}
                </AccountText>
                <StyledNavBarIcon
                  className="uil uil-ellipsis-v"
                  size="20px"
                  color={
                    theme.isVaultVariant
                      ? theme.newPalette.neutral.N800
                      : theme.atlas.semantic.color.type.default
                  }
                />
              </AccountTextContainer>
            )}
          </ProfileContainer>
          {settingsTooltip && (
            <SettingsTooltip
              root={settingsTooltipRoot as HTMLElement}
              closeTooltip={() => setSettingsTooltip(false)}
              variant={NavbarVariantEnum.PUBLIC}
              onLogout={onLogout}
              settingsOptions={settingsOptions}
              customPasswordLink={`/${organisation}/open-reporting/change-password`}
            />
          )}
        </div>
      )}
    </PublicNavbarBottomMenu>
  );
};

export const PublicNavbar = ({ organisation, currentUser }: Props) => {
  const { notifications } = useNotifications();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const items = notifications.data?.notifications ?? [];
    items.forEach((notification) => {
      if (
        notification.kind ===
          NotificationsType.DATA_SANITISATION_REPORTER_ANONYMISED &&
        notification.report
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- thunk typed against stale Types.State
        dispatch(fetchPublicMessages(notification.report) as any);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.data]);

  return (
    <BaseNavbar
      variant={NavbarVariantEnum.PUBLIC}
      topHeader="Open Reporting"
      bottomHeader="ACCOUNT"
      topNavList={<NavMenu organisation={organisation} />}
      bottomNavList={
        <AccountMenu currentUser={currentUser} organisation={organisation} />
      }
      secondaryDrawer={
        <NotificationsList
          notifications={notifications}
          variant={NavbarVariantEnum.PUBLIC}
          organisation={organisation}
        />
      }
      responsive={true}
    />
  );
};
